// exclude-from-index
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import Constants from './Flyout.constants';
import SVGIcon from '../../SVGIcon/SVGIcon';
import { locationsShape, patternsShape } from '../Search.shapes';
import iconMap from './Flyout.iconMap';
import { Text } from '../../Text';

import { StyledFlyout, FlyoutItem, LeftBlock, ItemContainer } from './Flyout.styles';
import { colors } from '../../../css';

const FlyoutListItem = React.memo(({ item, type, handleClickItem, locationsRefs, mapLocationTypeToIcon }) => {
  const { locationType, patternName, patternDescription, locationText } = item;
  const iconName = mapLocationTypeToIcon(locationType);
  const key = patternName ? patternName.replace(/ /g, '-') : locationText.replace(/ /g, '-');
  const headerTextColor = type === Constants.ResultsTypes.PATTERNS ? 'tertiary' : 'primary';
  const headerTextWeight = type === Constants.ResultsTypes.PATTERNS ? 'bold' : '';

  return (
    <FlyoutItem
      key={key}
      role="option"
      ref={(node) => {
        if (type !== Constants.ResultsTypes.PATTERNS && node) {
          locationsRefs.current.push(node);
        }
      }}
      tabIndex="0"
      type={type}
      noHover={type === Constants.ResultsTypes.PATTERNS}
      onMouseDown={() => handleClickItem(item)}
    >
      <LeftBlock>
        <SVGIcon
          selectedIcon={iconName}
          iconColor={type === Constants.ResultsTypes.PATTERNS ? colors.colorGrey03 : colors.colorGrey}
        />
      </LeftBlock>
      <ItemContainer type={type}>
        <Text.CopyParagraph color={headerTextColor} weight={headerTextWeight} noMargins>
          {patternName || locationText}
        </Text.CopyParagraph>
        {patternDescription && (
          <Text.CopyParagraph color="tertiary" noMargins>
            {patternDescription}
          </Text.CopyParagraph>
        )}
      </ItemContainer>
    </FlyoutItem>
  );
});

FlyoutListItem.propTypes = {
  item: PropTypes.shape({
    locationType: PropTypes.string,
    patternName: PropTypes.string,
    patternDescription: PropTypes.string,
    locationText: PropTypes.string
  }).isRequired,
  type: PropTypes.string.isRequired,
  handleClickItem: PropTypes.func.isRequired,
  locationsRefs: PropTypes.shape({
    current: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})])).isRequired
  }).isRequired,
  mapLocationTypeToIcon: PropTypes.func.isRequired
};

/**
 * Flyout component used by the Search component
 */
function Flyout(props) {
  const {
    closeFlyout,
    patterns,
    closeFlyoutAndFocusInput,
    searchFormatsLabel,
    onFlyoutItemClickHook,
    locations,
    ...others
  } = props;
  const [highlighted, setHighlighted] = useState(-1);
  const [sortedLocations, setSortedLocations] = useState([]);

  const locationsRefs = useRef([]);

  const handleClickItem = (value) => {
    if (onFlyoutItemClickHook) onFlyoutItemClickHook(value);
  };

  /**
   * Sort location data prioritizing Cities/Location types first,
   * and then sort alphabetically.
   */
  const sortLocations = (_newLocations = undefined) => {
    const newLocations = _newLocations || locations;
    const typeIsLocation = (type) => type === Constants.LocationTypes.LOCATION;
    const citiesOnly = newLocations.filter((loc) => typeIsLocation(loc.locationType));
    const notCities = newLocations.filter((loc) => !typeIsLocation(loc.locationType));

    if (!newLocations.length) return newLocations;
    return [
      ...citiesOnly.sort((a, b) => a.locationText.localeCompare(b.locationText)),
      ...notCities.sort((a, b) => a.locationText.localeCompare(b.locationText))
    ];
  };

  /**
   * Logic to handle different keydown events based on which key is pressed
   * This mimics native accessibility functionality:
   *
   * * Tab to focus on the element
   * * Spacebar to open the dropdown
   * * Arrow up/down to navigate
   * * Spacebar (while expanded) selects an option
   * * Escape (while expanded) collapses the dropdown
   *
   * @param {KeyboardEvent} e - KeyboardEvent passed in by document
   *
   */
  const handleKeyDownEvent = (e) => {
    const { key } = e;

    /** If we're actively selecting an item, disable the TAB key */
    if (key === 'Tab') {
      closeFlyout();
    }

    /** If we hit the Escape key and we're expanded, close up and clean our state */
    if (key === 'Escape') {
      /**
       * Preventing default here stops the ESC key from clearing the field
       * For some reason, this is the default behaviour for input[type=search]
       * Google's search fields don't do this and it seems annoying from a UX perspective
       */
      e.preventDefault();

      setHighlighted(-1);
      closeFlyoutAndFocusInput();
    }

    /** Capture arrow up and down and focus on each option as required. */
    if ((key === 'ArrowDown' || key === 'ArrowUp') && locationsRefs.current.length > 0) {
      e.preventDefault();
      let newValue;

      /**
       * If we hit the down arrow, change our highlighted value by +1
       * Otherwise, change it by -1
       */
      if (key === 'ArrowDown') {
        newValue = highlighted === sortedLocations.length - 1 ? 0 : highlighted + 1;
      } else if (highlighted === -1) {
        newValue = sortedLocations.length - 1;
      } else {
        newValue = highlighted === 0 ? sortedLocations.length - 1 : highlighted - 1;
      }

      /**
       * We're using an array to store references to the option nodes so we can
       * access them dynamically. Calling .focus() on the ref.
       */
      locationsRefs.current[newValue].focus();

      /**
       * Update our state with the new value of highlighted so we can compare it next time
       * one of the up/down arrow keys are hit.
       */
      setHighlighted(newValue);
    }

    /**
     * If we hit enter/spacebar, and we're expanded, set the currently focused element
     * as the selected one in our state.
     */
    if ((key === 'Enter' || key === ' ') && highlighted !== -1) {
      e.preventDefault();

      /** Don't reset 'focused' here since we're re-focusing on the DropdownHead in our callback */
      setHighlighted(-1);
      handleClickItem(sortedLocations[highlighted]);
      closeFlyoutAndFocusInput();
    }

    return false;
  };

  useEffect(() => {
    /**
     * Add document.eventListeners on componentMount
     * keydown - for accessibility purposes, allows navigation of th custom dropdown component
     */
    document.addEventListener('keydown', handleKeyDownEvent);

    /**
     * Cleanup for eventListeners when the component is unmounted
     */
    return () => document.removeEventListener('keydown', handleKeyDownEvent);
  }, []);

  useEffect(() => {
    setSortedLocations(sortLocations(locations));
  }, [locations]);

  /**
   * Take our location type and spit out an icon
   * This may be unnecessary abstraction but we're
   * shooting in the dark a bit at this stage.
   */
  const mapLocationTypeToIcon = (locationType) => {
    if (typeof locationType !== 'string') return iconMap.default;
    return iconMap[locationType.toLowerCase()] || iconMap.default;
  };

  /** Generate our Patterns, Locations, and then our full list to render */
  const locationsLoaded = sortedLocations.length > 0;
  const itemsToRender = [
    ...sortedLocations.map((item) => (
      <FlyoutListItem
        key={item.locationText || item.patternName}
        item={item}
        type={Constants.ResultsTypes.LOCATIONS}
        handleClickItem={handleClickItem}
        locationsRefs={locationsRefs}
        mapLocationTypeToIcon={mapLocationTypeToIcon}
      />
    )),
    ...patterns.map((item) => (
      <FlyoutListItem
        key={item.locationText || item.patternName}
        item={item}
        type={Constants.ResultsTypes.PATTERNS}
        handleClickItem={handleClickItem}
        locationsRefs={locationsRefs}
        mapLocationTypeToIcon={mapLocationTypeToIcon}
      />
    ))
  ];
  const loadingState = (
    <FlyoutItem key="nav-search-flyout-header" id="globalnav-search-flyout-header" noHover>
      <LeftBlock />
      <Text.CopyParagraph color="tertiary" noMargins>
        Loading...
      </Text.CopyParagraph>
    </FlyoutItem>
  );

  return (
    <StyledFlyout {...others}>
      {itemsToRender.length > 0 ? (
        <>
          {!locationsLoaded && (
            <FlyoutItem key="nav-search-flyout-header" id="globalnav-search-flyout-header" noHover>
              <LeftBlock />
              <Text.CopyParagraph color="tertiary" noMargins>
                {searchFormatsLabel}
              </Text.CopyParagraph>
            </FlyoutItem>
          )}
          {itemsToRender}
        </>
      ) : (
        loadingState
      )}
    </StyledFlyout>
  );
}

Flyout.propTypes = {
  /** Override Border Width */
  borderWidth: PropTypes.string,

  /** Boolean to turn off the flyout collapse animation */
  disableFlyoutAnimation: PropTypes.bool,

  /** an array of Locations (results) */
  locations: locationsShape,

  /** Disable box shadow */
  noShadow: PropTypes.bool,

  /** an array of Patterns (suggestions for searching) */
  patterns: patternsShape,

  /** position top setting for CSS */
  offsetTop: PropTypes.string,

  /** Hook called when a Flyout Item is clicked */
  onFlyoutItemClickHook: PropTypes.func,

  /** Set the overflow to visible instead of auto */
  overflowVisible: PropTypes.bool,

  /** Position Relative override */
  positionFixed: PropTypes.bool,

  /** Label for Search Formats to allow localization */
  searchFormatsLabel: PropTypes.string,

  closeFlyout: PropTypes.func,
  closeFlyoutAndFocusInput: PropTypes.func
};

Flyout.defaultProps = {
  borderWidth: undefined,
  disableFlyoutAnimation: false,
  locations: [],
  noShadow: false,
  patterns: [],
  offsetTop: undefined,
  onFlyoutItemClickHook: null,
  overflowVisible: false,
  positionFixed: false,
  searchFormatsLabel: 'Search Formats',
  closeFlyout: () => {},
  closeFlyoutAndFocusInput: () => {}
};

export default Flyout;
